import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  useTheme,
  alpha,
  styled,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Star as StarIcon,
  MoreVert as MoreVertIcon,
  DirectionsCar as CarIcon
} from '@mui/icons-material';
import { DriverOverview } from '../../services/dashboardService';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
}));

const StatusChip = styled(Chip)<{ status: 'available' | 'busy' | 'offline' }>(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'available': return theme.palette.success.main;
      case 'busy': return theme.palette.warning.main;
      case 'offline': return theme.palette.error.main;
      default: return theme.palette.grey[400];
    }
  };

  const color = getStatusColor();
  
  return {
    backgroundColor: alpha(color, 0.1),
    color: color,
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 24,
    '& .MuiChip-label': {
      padding: '0 8px',
    },
  };
});

const StyledListItem = styled(ListItem)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    transform: 'translateX(4px)',
  },
  '&:last-child': {
    marginBottom: 0,
  },
}));

const RatingBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  backgroundColor: alpha(theme.palette.warning.main, 0.1),
  color: theme.palette.warning.main,
  padding: theme.spacing(0.25, 0.75),
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.75rem',
  fontWeight: 600,
}));

interface DriversOverviewProps {
  drivers: DriverOverview[];
  title?: string;
  maxItems?: number;
}

export default function DriversOverview({ 
  drivers, 
  title = "Active Drivers",
  maxItems = 5 
}: DriversOverviewProps) {
  const theme = useTheme();

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available': return 'Available';
      case 'busy': return 'On Delivery';
      case 'offline': return 'Offline';
      default: return 'Unknown';
    }
  };

  const formatLastActive = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const displayedDrivers = drivers.slice(0, maxItems);

  return (
    <StyledCard>
      <CardHeader
        title={
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {title}
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            {drivers.length} total drivers
          </Typography>
        }
        action={
          <IconButton size="small">
            <MoreVertIcon />
          </IconButton>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 0, pb: 2 }}>
        <List sx={{ py: 0 }}>
          {displayedDrivers.map((driver) => (
            <StyledListItem key={driver.id} alignItems="flex-start">
              <ListItemAvatar>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  {driver.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {driver.name}
                    </Typography>
                    <StatusChip 
                      status={driver.status}
                      label={getStatusLabel(driver.status)}
                      size="small"
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                      <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {driver.location}
                      </Typography>
                    </Box>
                    
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center" gap={1}>
                        <RatingBox>
                          <StarIcon sx={{ fontSize: 12 }} />
                          <span>{driver.rating.toFixed(1)}</span>
                        </RatingBox>
                        
                        <Typography variant="caption" color="text.secondary">
                          {driver.totalDeliveries} deliveries
                        </Typography>
                      </Box>
                      
                      <Typography variant="caption" color="text.secondary">
                        {formatLastActive(driver.lastActive)}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </StyledListItem>
          ))}
        </List>

        {drivers.length > maxItems && (
          <Box textAlign="center" mt={2}>
            <Typography 
              variant="body2" 
              color="primary.main" 
              sx={{ 
                cursor: 'pointer',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              View all {drivers.length} drivers
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
}
